/**
 * Layered Slideshow Component
 * Adapted from Shopify Horizon theme for Impulse theme compatibility.
 * Standalone custom element - no ES module imports required.
 */

(function () {
  const DRAG_THRESHOLD = 5;
  const MAX_DRAG_WIDTH_RATIO = 0.8;
  const DRAG_COMPLETE_THRESHOLD = 0.5;
  const INACTIVE_SIZE = 56;
  const INACTIVE_MOBILE_SIZE = 44;
  const MOBILE_BREAKPOINT = 750;
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class LayeredSlideshowComponent extends HTMLElement {
    constructor() {
      super();
      this._active = 0;
      this._drag = null;
      this._abort = null;
      this._isMobile = false;
      this._heightObserver = null;
      this._contentObserver = null;
      this._containerObserver = null;
      this._mediaQuery = window.matchMedia('(min-width: ' + MOBILE_BREAKPOINT + 'px)');
      this._handleMediaQueryChange = this._onMediaQueryChange.bind(this);
    }

    get _inactiveSize() {
      return this._isMobile ? INACTIVE_MOBILE_SIZE : INACTIVE_SIZE;
    }

    connectedCallback() {
      this._container = this.querySelector('[ref="container"]');
      this._tabs = Array.from(this.querySelectorAll('[ref="tabs[]"]'));
      this._panels = Array.from(this.querySelectorAll('[ref="panels[]"]'));

      if (!this._tabs.length || !this._container) return;

      this._active = Math.max(
        0,
        this._tabs.findIndex(function (t) {
          return t.getAttribute('aria-selected') === 'true';
        })
      );

      this._isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      this._mediaQuery.addEventListener('change', this._handleMediaQueryChange);

      var self = this;
      this._containerObserver = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.contentBoxSize) {
            var boxSize = entry.contentBoxSize[0];
            var size;
            if (boxSize) {
              size = self._isMobile ? boxSize.blockSize : boxSize.inlineSize;
            } else {
              size = self._isMobile ? entry.contentRect.height : entry.contentRect.width;
            }
            self._updateGridSizes(size);
          }
        }
      });
      this._containerObserver.observe(this._container);

      this._updateActiveTab();
      this._setupEventListeners();
      this._observeContentHeight();
    }

    _setupEventListeners() {
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      var opts = { signal: this._abort.signal };
      var self = this;

      this.addEventListener('keydown', function (e) { self._handleKeydown(e); }, opts);

      this._tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function (e) { self._handleTabClick(e, i); }, opts);
        tab.addEventListener('focus', function (e) { self._handleTabFocus(e, i); }, opts);
      });

      this._setupPanelFocusManagement(opts);

      if (!this._isMobile) {
        this._container.addEventListener('pointerdown', function (e) { self._startDrag(e); }, opts);
        this._container.addEventListener('click', function (e) { self._preventClickDuringDrag(e); },
          Object.assign({}, opts, { capture: true })
        );
      }
    }

    _handleKeydown(e) {
      var target = e.target;
      if (target.getAttribute('role') !== 'tab') return;

      var tabs = this._tabs;
      if (!tabs) return;

      var i = tabs.indexOf(target);
      var navMap = {};
      navMap[this._isMobile ? 'ArrowUp' : 'ArrowLeft'] = -1;
      navMap[this._isMobile ? 'ArrowDown' : 'ArrowRight'] = 1;
      navMap['Home'] = -i;
      navMap['End'] = tabs.length - 1 - i;

      var offset = navMap[e.key];
      if (offset !== undefined) {
        e.preventDefault();
        var nextIndex = (i + offset + tabs.length) % tabs.length;
        if (tabs[nextIndex]) tabs[nextIndex].focus();
        this._activate(nextIndex);
      }
    }

    _handleTabClick(e, index) {
      e.preventDefault();
      this._activate(index);
    }

    _handleTabFocus(e, index) {
      var target = e.target;
      if (target.matches(':focus-visible')) {
        this._activate(index);
      }
    }

    _setupPanelFocusManagement(opts) {
      var panels = this._panels;
      if (!panels) return;
      var self = this;

      panels.forEach(function (panel, index) {
        panel.addEventListener('keydown', function (event) {
          self._handlePanelKeydown(event, index);
        }, opts);
      });
    }

    _handlePanelKeydown(event, index) {
      if (event.key !== 'Tab') return;

      var panels = this._panels;
      var panel = event.currentTarget;
      var focusable = this._getFocusableElements(panel);
      var firstFocusable = focusable[0];
      var lastFocusable = focusable[focusable.length - 1];

      if (event.shiftKey) {
        var isAtStart =
          (firstFocusable && document.activeElement === firstFocusable) ||
          (!focusable.length && document.activeElement === panel);
        if (isAtStart && index > 0) {
          event.preventDefault();
          this._activate(index - 1);
          this._focusPanelEdge(index - 1, 'end');
        }
        return;
      }

      var isAtEnd =
        (lastFocusable && document.activeElement === lastFocusable) ||
        (!focusable.length && document.activeElement === panel);

      if (isAtEnd && panels && index < panels.length - 1) {
        event.preventDefault();
        this._activate(index + 1);
        this._focusPanelEdge(index + 1, 'start');
      }
    }

    _focusPanelEdge(index, position) {
      position = position || 'start';
      var panel = this._panels[index];
      if (!panel) return;

      var focusable = this._getFocusableElements(panel);
      var target = position === 'end' ? focusable[focusable.length - 1] : focusable[0];

      requestAnimationFrame(function () { (target || panel).focus(); });
    }

    _getFocusableElements(panel) {
      return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(function (el) { return !el.closest('[inert]'); });
    }

    _preventClickDuringDrag(e) {
      var target = e.target;
      if (this._drag && this._drag.prevent && target.closest('[role="tab"]')) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }

    _onMediaQueryChange() {
      var wasMobile = this._isMobile;
      this._isMobile = window.innerWidth < MOBILE_BREAKPOINT;

      if (wasMobile !== this._isMobile) {
        var container = this._container;
        container.setAttribute('data-instant-transitions', '');

        this._clearHeightStyles();
        this._observeContentHeight();
        this._updateActiveTab();
        this._setupEventListeners();

        requestAnimationFrame(function () {
          container.removeAttribute('data-instant-transitions');
        });
      }
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._heightObserver) this._heightObserver.disconnect();
      this._heightObserver = null;
      if (this._contentObserver) this._contentObserver.disconnect();
      this._contentObserver = null;
      if (this._containerObserver) this._containerObserver.disconnect();
      this._containerObserver = null;
      this._mediaQuery.removeEventListener('change', this._handleMediaQueryChange);
    }

    select(index, options) {
      options = options || {};
      this._activate(index, options.instant || false);
    }

    _activate(index, instant) {
      instant = instant || false;
      var container = this._container;
      var tabs = this._tabs;
      if (!tabs || index === this._active || index < 0 || index >= tabs.length) return;

      if (instant) {
        container.setAttribute('data-instant-transitions', '');
      }

      this._active = index;
      this._updateActiveTab();

      if (instant) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            container.removeAttribute('data-instant-transitions');
          });
        });
      }
    }

    _updateActiveTab() {
      var tabs = this._tabs;
      var panels = this._panels;
      var active = this._active;

      if (tabs) {
        tabs.forEach(function (tab, i) {
          var isActive = i === active;
          tab.setAttribute('aria-selected', String(isActive));
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      }

      if (panels) {
        panels.forEach(function (panel, i) {
          var isActive = i === active;
          if (isActive) {
            panel.removeAttribute('inert');
          } else {
            panel.setAttribute('inert', '');
          }
          panel.setAttribute('tabindex', isActive ? '0' : '-1');

          var video = panel.querySelector('video');
          if (video) {
            if (isActive) {
              video.play().catch(function() {});
            } else {
              video.pause();
            }
          }
        });
      }

      this._updateGridSizes();
    }

    _updateGridSizes(containerSize) {
      var container = this._container;
      var tabs = this._tabs;
      if (!tabs) return;
      var inactiveSize = this._inactiveSize;
      var size = containerSize != null
        ? containerSize
        : (this._isMobile
          ? container.getBoundingClientRect().height
          : container.getBoundingClientRect().width);
      var activeSize = size - inactiveSize * (tabs.length - 1);
      var active = this._active;
      var sizes = tabs.map(function (_, i) {
        return i === active ? activeSize + 'px' : inactiveSize + 'px';
      });
      container.style.setProperty('--active-tab', sizes.join(' '));
    }

    _startDrag(event) {
      if (this._isMobile) return;

      var tabs = this._tabs;
      if (!tabs) return;

      var eventTarget = event.target;
      var tab = eventTarget.closest('[role="tab"]');
      if (tab) {
        var i = tabs.indexOf(tab);
        if (i === this._active) return;
        var target = i > this._active ? i + 1 : i;
        if (target >= tabs.length) return;
        this._initializeDrag(event, target);
      } else {
        this._initializeDrag(event);
      }
    }

    _initializeDrag(event, initialTarget) {
      var container = this._container;
      var tabs = this._tabs;
      if (!tabs) return;

      var containerWidth = container.getBoundingClientRect().width;
      var inactiveSize = this._inactiveSize;
      var activeSize = containerWidth - inactiveSize * (tabs.length - 1);

      this._drag = {
        target: initialTarget != null ? initialTarget : -1,
        start: event.clientX,
        max: containerWidth * MAX_DRAG_WIDTH_RATIO,
        activeSize: activeSize,
        left: initialTarget !== undefined ? initialTarget > this._active : false
      };

      var ac = new AbortController();
      var opts = { signal: ac.signal };
      var self = this;

      document.addEventListener('pointermove', function (e) { self._handleDrag(e); }, opts);
      document.addEventListener('pointerup', function () { self._endDrag(ac); }, opts);
      document.addEventListener('pointercancel', function () { self._endDrag(ac); }, opts);

      event.preventDefault();
    }

    _handleDrag(event) {
      if (!this._drag) return;

      var container = this._container;
      var tabs = this._tabs;
      if (!container || !tabs) return;

      var delta = event.clientX - this._drag.start;
      var move = Math.abs(delta);

      if (!this._drag.dragging && move >= DRAG_THRESHOLD) {
        if (this._drag.target === -1) {
          if (delta > 0 && this._active > 0) {
            this._drag.target = this._active - 1;
            this._drag.left = false;
          } else if (delta < 0 && this._active < tabs.length - 1) {
            this._drag.target = this._active + 1;
            this._drag.left = true;
          } else {
            return;
          }
        }
        this._drag.dragging = true;
        container.setAttribute('data-dragging', '');
      }

      if (!this._drag.dragging) return;

      var correct = this._drag.left ? delta < 0 : delta > 0;
      var progress = correct ? Math.min(move / this._drag.max, 1) : 0;

      var inactiveSize = this._inactiveSize;
      var activeSize = this._drag.activeSize;
      var range = activeSize - inactiveSize;
      var active = this._active;
      var drag = this._drag;
      var sizes = tabs.map(function (_, i) {
        if (i === active) {
          var sz = Math.max(inactiveSize, activeSize - range * progress);
          return sz + 'px';
        }
        if (i === drag.target) {
          var dsz = inactiveSize + range * progress;
          return dsz + 'px';
        }
        return inactiveSize + 'px';
      });

      container.style.setProperty('--active-tab', sizes.join(' '));
      this._drag.progress = progress;
    }

    _endDrag(ac) {
      if (!this._drag) return;

      var container = this._container;
      if (container) container.removeAttribute('data-dragging');

      if (this._drag.dragging) {
        this._drag.prevent = true;
        var self = this;
        setTimeout(function () { self._drag = null; }, 100);

        if (this._drag.progress && this._drag.progress >= DRAG_COMPLETE_THRESHOLD) {
          this._activate(this._drag.target);
        } else {
          this._updateActiveTab();
        }
      } else {
        this._drag = null;
      }

      ac.abort();
    }

    _observeContentHeight() {
      var panels = this._panels;
      var self = this;

      if (this._heightObserver) this._heightObserver.disconnect();
      this._heightObserver = new ResizeObserver(function () { self._syncHeight(); });

      if (this._contentObserver) this._contentObserver.disconnect();
      this._contentObserver = new MutationObserver(function () { self._syncHeight(); });

      if (panels) {
        panels.forEach(function (panel) {
          var content = panel.querySelector('.layered-slideshow__content');
          var inner = content ? content.querySelector('.group-block-content') : null;

          if (inner) self._heightObserver.observe(inner);
          if (content) self._heightObserver.observe(content);

          var target = inner || content;
          if (target) {
            self._contentObserver.observe(target, {
              childList: true,
              subtree: true,
              characterData: true
            });
          }
        });
      }

      this._syncHeight();
    }

    _syncHeight() {
      var container = this._container;
      var contentHeight = this._getMaxContentHeight();
      var isAuto = container.getAttribute('size') === 'auto';

      if (this._isMobile) {
        this._syncMobileHeight(contentHeight, isAuto);
      } else {
        this._syncDesktopHeight(contentHeight, isAuto);
      }
    }

    _syncDesktopHeight(contentHeight, isAuto) {
      var container = this._container;

      if (isAuto) {
        var minHeightTemp = Math.max(contentHeight, 150);
        container.style.height = minHeightTemp + 'px';
        this.style.minHeight = minHeightTemp + 'px';
      } else {
        var savedMinHeight = this.style.minHeight;
        this.style.minHeight = '';
        var cssMinHeight = parseFloat(getComputedStyle(this).minHeight) || 0;
        this.style.minHeight = savedMinHeight;

        if (contentHeight > cssMinHeight) {
          this.style.minHeight = contentHeight + 'px';
          container.style.height = contentHeight + 'px';
        } else {
          this.style.minHeight = '';
          container.style.height = '';
        }
      }
    }

    _syncMobileHeight(contentHeight, isAuto) {
      var container = this._container;
      var tabs = this._tabs;
      if (!container || !tabs) return;

      var containerStyles = getComputedStyle(container);
      var inactiveStackHeight = (tabs.length - 1) * this._inactiveSize;

      var minPanelHeight;
      if (isAuto) {
        minPanelHeight = 150;
      } else {
        var inheritedValue = containerStyles.getPropertyValue('--layered-panel-height-mobile');
        var componentValue = getComputedStyle(this).getPropertyValue('--layered-panel-height-mobile');
        minPanelHeight = parseFloat(inheritedValue || componentValue) || 260;
      }

      var requiredActiveHeight = Math.max(minPanelHeight, contentHeight);
      container.style.setProperty('--active-panel-height', requiredActiveHeight + 'px');
      container.style.height = (requiredActiveHeight + inactiveStackHeight) + 'px';
    }

    _clearHeightStyles() {
      var container = this._container;
      this.style.minHeight = '';
      container.style.height = '';
      container.style.removeProperty('--active-panel-height');
    }

    _getMaxContentHeight() {
      var panels = this._panels;
      var max = 0;

      if (panels) {
        panels.forEach(function (panel) {
          var content = panel.querySelector('.layered-slideshow__content');
          if (!content) return;

          var inner = content.querySelector('.group-block-content') || content;
          var savedHeight = inner.style.height;
          inner.style.height = 'auto';

          var styles = getComputedStyle(content);
          var paddingTop = parseFloat(styles.paddingBlockStart || styles.paddingTop) || 0;
          var paddingBottom = parseFloat(styles.paddingBlockEnd || styles.paddingBottom) || 0;

          var height = (inner.scrollHeight || 0) + paddingTop + paddingBottom;
          if (height > max) max = height;

          inner.style.height = savedHeight;
        });
      }

      return max;
    }
  }

  if (!customElements.get('layered-slideshow-component')) {
    customElements.define('layered-slideshow-component', LayeredSlideshowComponent);
  }
})();
